import { useCallback, useEffect, useSyncExternalStore } from 'react';
import { getCampAvailabilityFun } from '../api/infoAPI';

/*
 * Free rooms / tents of each unit of a camp for the picked dates.
 *
 * The booking tab owns the dates, so it loads it:
 *   const { availability, refresh } = useCampAvailability(listingId, { checkin, checkout, pets })
 * Other parts of the same page (the "Rooms & units" section) only read it:
 *   const availability = useCampAvailabilityView(listingId)
 *
 * availability: null (no dates yet / couldn't load — show the normal stock) or
 *   { status: 'loading' | 'ready', checkin, checkout, bookable, reason, fewLeft,
 *     units: { [unitId]: free count } }
 *
 * refresh() asks the server again (Book Now) and resolves with the fresh
 * result, or null when it couldn't be checked (the server checks again on
 * payment anyway, so the guest is never blocked by a failed check).
 */

const store = new Map(); // listingId -> availability
const listeners = new Set();
const emit = () => listeners.forEach((listener) => listener());
const subscribe = (listener) => {
    listeners.add(listener);
    return () => listeners.delete(listener);
};
const keyOf = ({ checkin, checkout, pets }) => `${checkin}|${checkout}|${pets || 0}`;

const setEntry = (listingId, entry) => {
    if (entry) store.set(listingId, entry);
    else store.delete(listingId);
    emit();
};

// the latest request per camp wins (dates changed while one was loading)
const latest = new Map();

async function load(listingId, stay, { quiet = false } = {}) {
    const key = keyOf(stay);
    latest.set(listingId, key);
    const current = store.get(listingId);
    if (!quiet || current?.key !== key) {
        // keep showing the old numbers for the same dates while checking again
        setEntry(listingId, { ...(current?.key === key ? current : {}), key, ...stay, status: 'loading' });
    }
    try {
        const { data } = await getCampAvailabilityFun(listingId, stay);
        if (latest.get(listingId) !== key) return null;
        // not a proper answer: treat like a failed check (show the normal stock)
        if (!data?.success || typeof data.units !== 'object') throw new Error('Bad availability response');
        const entry = {
            key,
            ...stay,
            status: 'ready',
            bookable: Boolean(data?.bookable),
            reason: data?.reason || null,
            fewLeft: data?.fewLeft || 3,
            units: data?.units || {}
        };
        setEntry(listingId, entry);
        return entry;
    } catch (error) {
        if (latest.get(listingId) === key) setEntry(listingId, null);
        return null;
    }
}

/** Read-only view for other parts of the camp page. */
export function useCampAvailabilityView(listingId) {
    return useSyncExternalStore(subscribe, () => (listingId ? store.get(String(listingId)) || null : null));
}

/** For the booking tab: loads whenever the dates change. */
export function useCampAvailability(listingId, { checkin, checkout, pets = 0 }) {
    const id = listingId ? String(listingId) : null;
    const ready = Boolean(id && checkin && checkout && checkout > checkin);

    useEffect(() => {
        if (!id) return undefined;
        if (!ready) {
            latest.delete(id);
            setEntry(id, null);
            return undefined;
        }
        load(id, { checkin, checkout, pets });
        return undefined;
    }, [id, ready, checkin, checkout, pets]);

    // the page is left: forget it so another camp page starts clean
    useEffect(() => () => id && setEntry(id, null), [id]);

    const refresh = useCallback(
        () => (ready ? load(id, { checkin, checkout, pets }, { quiet: true }) : Promise.resolve(null)),
        [id, ready, checkin, checkout, pets]
    );

    return { availability: useCampAvailabilityView(id), refresh };
}

/** Free count of a unit, or null when not known (no dates / not loaded). */
export const leftFor = (availability, unitId) => {
    if (availability?.status !== 'ready' || !unitId) return null;
    return Number(availability.units?.[String(unitId)] ?? 0);
};
